/** @jsxImportSource @emotion/react */
import React, { useCallback, useState } from 'react'
import estyled from '@signal/theme/emotion'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import LeftMenuNew from '@signal/components/LeftMenuNew'
import AppFooter from '@purecarslabs/signal-components/baseline/AppFooter'
import ChromeHeader from '@purecarslabs/signal-components/baseline/ChromeHeader'
import {
  getHeaderContentHeight,
  getHideChromeMode
} from '@purecarslabs/signal-layouts/utils'
import Page from '@purecarslabs/signal-layouts/baseline/Page'
import isCurrentPathForAnonymousAccess from '@purecarslabs/signal-utils/isAnonymousPath'
import CommonPageLoader from '@signal/components/CommonPage/Loader'
import { AccountChangeHandlerType } from '@signal/core/user/types'
import { AnyReactComponentType } from '@signal/utils/types'
import { CommonPageLoaderPropsType } from '@signal/components/CommonPage/types'
import { useMenuPanelWidth } from '@signal/layouts/hooks'

const PageComponentWrapper = estyled.div<{
  hideMenu?: boolean
  menuPanelWidth: string
}>`
  height: 100%;
  margin-left: ${({ hideMenu, menuPanelWidth }) =>
    hideMenu ? 0 : menuPanelWidth};
  transition: margin-left 0.3s ease-in-out;
`

const BaselinePage: React.FC<{
  routeProps: React.PropsWithChildren<RouteComponentProps>
  withPageParams: WithPageParamsType
}> = ({
  routeProps: { children, history, match, ...rest },
  withPageParams: {
    PageComponent,
    loaderProps = {} as CommonPageLoaderPropsType,
    headerProps = {} as Record<string, unknown>,
    HeaderComponent = ChromeHeader
  }
}) => {
  // @ts-ignore
  const accountId = match.params.accountId
  const { appMinWidth, hideChrome, hideFooter, hideMenu } = loaderProps
  const hideChromeUse =
    hideChrome || getHideChromeMode() || isCurrentPathForAnonymousAccess()
  const [onAccountChange, setOnAccountChange] = useState<
    AccountChangeHandlerType
  >()
  const handleOnNeedOnAccountChange = useCallback(
    (onChange: AccountChangeHandlerType) => {
      setOnAccountChange(() => onChange)
    },
    []
  )

  const menuPanelWidth = useMenuPanelWidth()

  return (
    <CommonPageLoader
      accountId={accountId}
      onAccountChange={onAccountChange}
      {...loaderProps}
    >
      <Page
        appMinWidth={appMinWidth}
        defaultHeaderHeight={parseInt(getHeaderContentHeight())}
        header={
          // @ts-ignore
          <HeaderComponent
            hideChrome={hideChromeUse}
            onNeedOnAccountChange={handleOnNeedOnAccountChange}
            {...headerProps}
          />
        }
        left={
          hideChrome || hideMenu ? undefined : <LeftMenuNew {...headerProps} />
        }
        footer={
          <AppFooter
            // @ts-ignore
            everNavigated={history?.everNavigated}
            hideChrome={hideChromeUse}
            hideFooter={hideFooter}
            hideMenu={hideMenu}
            menuPanelWidth={menuPanelWidth}
          />
        }
      >
        <PageComponentWrapper
          hideMenu={hideMenu}
          menuPanelWidth={menuPanelWidth}
        >
          <PageComponent
            match={match}
            menuPanelWidth={menuPanelWidth}
            {...rest}
          >
            {children}
          </PageComponent>
        </PageComponentWrapper>
      </Page>
    </CommonPageLoader>
  )
}

export type WithPageParamsType = {
  headerProps?: Record<string, unknown>
  loaderProps?: CommonPageLoaderPropsType
  withRouterFunc?: typeof withRouter
  HeaderComponent?: AnyReactComponentType
  PageComponent: AnyReactComponentType
}

// this hoc wraps the given component in a baseline Page layout component.  The standard chrome and footer
// will be provided however the header component and its properties can be overridden
const withPage = (params: WithPageParamsType) => {
  const withRouterFunc = params.withRouterFunc || withRouter
  return withRouterFunc(
    (routeProps: React.PropsWithChildren<RouteComponentProps>) => (
      <BaselinePage routeProps={routeProps} withPageParams={params} />
    )
  )
}

export default withPage
